import React, { useCallback, useEffect, useState } from 'react';
import { Icon } from '@iconify/react';
import Breadcrumb from '../../../components/shared/Breadcrumb';

const EMPTY_FORM = { codigo: '', nombre: '', descripcion: '', estado: 'activo' };

function headers() {
  const token = sessionStorage.getItem('syrakis_token');
  return {
    Accept: 'application/json',
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
}

async function request(url, options) {
  const response = await fetch(url, { credentials: 'same-origin', headers: headers(), ...options });
  const data = await response.json();
  if (!response.ok) throw new Error(data.message || data.error || 'No se pudo completar la operación.');
  return data;
}

export default function ProgramasPage() {
  const [programas, setProgramas] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, page_size: 10, total: 0 });
  const [search, setSearch] = useState('');
  const [estadoFilter, setEstadoFilter] = useState('');
  const [form, setForm] = useState(EMPTY_FORM);
  const [editing, setEditing] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  const load = useCallback(async (page = 1) => {
    setError('');
    const params = new URLSearchParams({ page: String(page), page_size: '10', search, estado: estadoFilter });
    try {
      const data = await request(`/api/aos/programas?${params.toString()}`);
      setProgramas(data.programas || []);
      setPagination(data.pagination || { page, page_size: 10, total: 0 });
    } catch (reason) {
      setError(reason.message);
    }
  }, [search, estadoFilter]);

  useEffect(() => { load(1); }, [load]);

  function openCreate() {
    setEditing(null);
    setForm(EMPTY_FORM);
    setShowForm(true);
    setError('');
    setMessage('');
  }

  function openEdit(programa) {
    setEditing(programa);
    setForm({ codigo: programa.codigo, nombre: programa.nombre, descripcion: programa.descripcion || '', estado: programa.estado });
    setShowForm(true);
    setError('');
    setMessage('');
  }

  async function save(event) {
    event.preventDefault();
    setBusy(true);
    setError('');
    setMessage('');
    try {
      await request(editing ? `/api/aos/programas/${editing.id}` : '/api/aos/programas', {
        method: editing ? 'PUT' : 'POST',
        body: JSON.stringify(form),
      });
      setShowForm(false);
      setMessage(editing ? 'Programa actualizado.' : 'Programa creado.');
      await load(pagination.page);
    } catch (reason) {
      setError(reason.message);
    } finally {
      setBusy(false);
    }
  }

  async function deactivate(programa) {
    if (!window.confirm(`¿Desactivar el programa ${programa.nombre}? Se conservará su historial.`)) return;
    setBusy(true);
    setError('');
    setMessage('');
    try {
      await request(`/api/aos/programas/${programa.id}`, { method: 'DELETE' });
      setMessage('Programa desactivado.');
      await load(pagination.page);
    } catch (reason) {
      setError(reason.message);
    } finally {
      setBusy(false);
    }
  }

  const pageCount = Math.max(1, Math.ceil(pagination.total / pagination.page_size));

  return <>
    <Breadcrumb title="Programas Sercotec" />
    <div className="dashboard-main-body">
      <div className="d-flex flex-wrap justify-content-between align-items-center gap-3 mb-20">
        <div>
          <h4 className="mb-8">Programas AOS Sercotec</h4>
          <p className="text-secondary-light mb-0">Administra el catálogo de programas de este tenant. La cobertura territorial se define en cada convocatoria.</p>
        </div>
        <button type="button" className="btn btn-primary d-inline-flex align-items-center gap-2" onClick={openCreate}>
          <Icon icon="solar:add-circle-linear" /> Nuevo programa
        </button>
      </div>

      {error && <div className="alert alert-danger" role="alert">{error}</div>}
      {message && <div className="alert alert-success" role="status">{message}</div>}

      <div className="card mb-20">
        <div className="card-body">
          <div className="row g-3 align-items-end">
            <div className="col-md-7">
              <label className="form-label" htmlFor="aos-program-search">Buscar</label>
              <input id="aos-program-search" className="form-control" value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Código, nombre o descripción" />
            </div>
            <div className="col-md-3">
              <label className="form-label" htmlFor="aos-program-status">Estado</label>
              <select id="aos-program-status" className="form-select" value={estadoFilter} onChange={(event) => setEstadoFilter(event.target.value)}>
                <option value="">Todos</option><option value="activo">Activo</option><option value="inactivo">Inactivo</option>
              </select>
            </div>
            <div className="col-md-2">
              <button type="button" className="btn btn-outline-primary w-100" onClick={() => load(1)}><Icon icon="solar:magnifer-linear" className="me-1" />Buscar</button>
            </div>
          </div>
        </div>
      </div>

      <div className="card">
        <div className="table-responsive">
          <table className="table bordered-table mb-0 align-middle">
            <thead><tr><th>Código</th><th>Nombre</th><th>Descripción</th><th>Estado</th><th className="text-end">Acciones</th></tr></thead>
            <tbody>
              {programas.map((programa) => <tr key={programa.id}>
                <td className="fw-semibold">{programa.codigo}</td><td>{programa.nombre}</td><td>{programa.descripcion || '—'}</td>
                <td><span className={`badge ${programa.estado === 'activo' ? 'bg-success-50 text-success-600' : 'bg-secondary-100 text-secondary-600'}`}>{programa.estado === 'activo' ? 'Activo' : 'Inactivo'}</span></td>
                <td className="text-end text-nowrap">
                  <button type="button" className="btn btn-sm btn-outline-primary me-2" disabled={busy} onClick={() => openEdit(programa)}>Editar</button>
                  {programa.estado === 'activo' && <button type="button" className="btn btn-sm btn-outline-danger" disabled={busy} onClick={() => deactivate(programa)}>Desactivar</button>}
                </td>
              </tr>)}
              {!programas.length && <tr><td colSpan="5" className="text-center py-32 text-secondary-light">No hay programas para mostrar.</td></tr>}
            </tbody>
          </table>
        </div>
        <div className="card-footer d-flex justify-content-between align-items-center">
          <span className="text-secondary-light">{pagination.total} programa(s)</span>
          <div className="d-flex align-items-center gap-2">
            <button type="button" className="btn btn-sm btn-light" disabled={pagination.page <= 1} onClick={() => load(pagination.page - 1)}>Anterior</button>
            <span className="small">{pagination.page} / {pageCount}</span>
            <button type="button" className="btn btn-sm btn-light" disabled={pagination.page >= pageCount} onClick={() => load(pagination.page + 1)}>Siguiente</button>
          </div>
        </div>
      </div>

      {showForm && <div className="modal d-block" role="dialog" aria-modal="true" aria-labelledby="aos-program-form-title" style={{ background: 'rgba(0,0,0,.45)' }}>
        <div className="modal-dialog modal-dialog-centered"><div className="modal-content">
          <form onSubmit={save}>
            <div className="modal-header"><h5 className="modal-title" id="aos-program-form-title">{editing ? 'Editar programa' : 'Nuevo programa'}</h5><button type="button" className="btn-close" aria-label="Cerrar" onClick={() => setShowForm(false)} /></div>
            <div className="modal-body">
              <div className="mb-3"><label className="form-label" htmlFor="aos-program-code">Código</label><input id="aos-program-code" className="form-control" maxLength="50" value={form.codigo} onChange={(event) => setForm({ ...form, codigo: event.target.value.toUpperCase() })} required /></div>
              <div className="mb-3"><label className="form-label" htmlFor="aos-program-name">Nombre</label><input id="aos-program-name" className="form-control" maxLength="180" value={form.nombre} onChange={(event) => setForm({ ...form, nombre: event.target.value })} required /></div>
              <div className="mb-3"><label className="form-label" htmlFor="aos-program-description">Descripción</label><textarea id="aos-program-description" className="form-control" rows="3" value={form.descripcion} onChange={(event) => setForm({ ...form, descripcion: event.target.value })} /></div>
              <div className="mb-3"><label className="form-label" htmlFor="aos-program-state">Estado</label><select id="aos-program-state" className="form-select" value={form.estado} onChange={(event) => setForm({ ...form, estado: event.target.value })}><option value="activo">Activo</option><option value="inactivo">Inactivo</option></select></div>
            </div>
            <div className="modal-footer"><button type="button" className="btn btn-light" disabled={busy} onClick={() => setShowForm(false)}>Cancelar</button><button type="submit" className="btn btn-primary" disabled={busy}>{busy ? 'Guardando…' : 'Guardar programa'}</button></div>
          </form>
        </div></div>
      </div>}
    </div>
  </>;
}
